import type {
  CreateTicketReplyResponse,
  CreateTicketRequest,
  CreateTicketResponse,
  ListTicketAttachmentsResponse,
  ListTicketRepliesResponse,
  ListTicketsResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  createTicket,
  createTicketReply,
  listTicketAttachments,
  listTicketReplies,
  listTickets,
} from './tickets.ts';
import type { ListTicketsParams } from './tickets.ts';

/**
 * React Query wrappers around `/v1/tickets` (tenant #116).
 *
 * Query key conventions:
 *   - `['tickets', 'list', status, offset]`        — one page of the list
 *   - `['tickets', 'replies', ticketId]`           — one ticket's thread
 *   - `['tickets', 'attachments', ticketId]`       — one ticket's attachment metadata
 *
 * Mutation invalidation map:
 *   - createTicket      → invalidate `['tickets']`
 *   - createTicketReply → invalidate `['tickets']`
 *
 * Both mutations invalidate the WHOLE `['tickets']` prefix on purpose.
 * A reply can change the ticket itself, not just its thread: the server
 * auto-reopens `resolved → processing` in the same transaction, so
 * refreshing the thread alone would leave the row's status chip lying
 * about a state the server already left.
 */

export const TICKETS_QUERY_KEY = ['tickets'] as const;

export function useTicketsList(
  params: ListTicketsParams,
  enabled = true,
): UseQueryResult<ListTicketsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'list', params.status, params.offset],
    queryFn: ({ signal }) => listTickets(params, fetchImpl, signal),
    enabled,
  });
}

export function useTicketReplies(
  ticketId: string,
  enabled = true,
): UseQueryResult<ListTicketRepliesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'replies', ticketId],
    queryFn: ({ signal }) => listTicketReplies(ticketId, fetchImpl, signal),
    enabled,
  });
}

/**
 * Attachment *metadata* only. Callers that need pixels fetch content
 * bytes separately through `getTicketAttachmentContent` so the list
 * page never N+1s — pass `enabled` only when the row is expanded and
 * `attachmentCount > 0`.
 */
export function useTicketAttachments(
  ticketId: string,
  enabled = true,
): UseQueryResult<ListTicketAttachmentsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...TICKETS_QUERY_KEY, 'attachments', ticketId],
    queryFn: ({ signal }) => listTicketAttachments(ticketId, fetchImpl, signal),
    enabled,
  });
}

export function useCreateTicket(): UseMutationResult<
  CreateTicketResponse,
  Error,
  CreateTicketRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createTicket(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
    },
  });
}

export interface CreateTicketReplyVars {
  readonly ticketId: string;
  readonly content: string;
}

export function useCreateTicketReply(): UseMutationResult<
  CreateTicketReplyResponse,
  Error,
  CreateTicketReplyVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, content }) => createTicketReply(ticketId, { content }, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
    },
  });
}
